import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
  } from '@angular/core';
  import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
  } from '@angular/forms';
  import { select, Store } from '@ngrx/store';
  import { combineLatest, Observable, Subject } from 'rxjs';
  import {
    distinctUntilChanged,
    filter,
    take,
    takeUntil,
    withLatestFrom,
  } from 'rxjs/operators';
  import {
    selectActiveAchIncomingInstructions,
    selectActiveOnRequestAndRecurringAchInstructions,
    selectIsIip,
    selectIsPtrs,
    selectSubAccountId,
  } from '../../../account-info-header/reducers';
  import {
    selectStandingAuthTableEditTransactionClickedACH,
    selectStandingAuthTableStartTransactionClickedACH,
  } from '../../../shared/reducers';
  import { ComponentState } from '../../../shared/types/ComponentState';
  import { StandingInstruction } from '../../../shared/types/StandingInstruction';
  import {
    selectAchInquiryCallFinished,
    selectStandingAuthForm,
    selectStandingAuthFormDirection,
  } from '../../../standing-authorizations/reducers';
  import {
    AchExtraPrompts,
    StandingAuthorizationForm,
  } from '../../models/standingAuthorizationForm';
  import {
    selectIsFromEdit,
    selectStandingAuthCallSucceededAch,
    selectStandingAuthId,
  } from '../../../create-request/reducers';
  import {
    PtrsActions,
    StandingAuthorizationsTableActions,
  } from '../../../create-request/actions';
  import { StandingAuthFormActions } from '../../actions';
  import { buildFromPrefilledForm } from '../../../create-request/utilities/formUtilities';
  import { AchInfo } from '../../../create-request/models/ach/AchInfo';
import { RadioControl } from '../../../create-request/models/form/FormControlRadio';
  
  @Component({
  standalone: false,
    selector: 'mm-sa-ach-extra-prompts',
    templateUrl: './sa-ach-extra-prompts.component.html',
    styleUrls: ['./sa-ach-extra-prompts.component.scss'],
  })
  export class SaAchExtraPromptsComponent implements OnInit, OnDestroy {
    @Input() parentForm!: UntypedFormGroup;
    @Input() prefilledPrompts: AchExtraPrompts | undefined;
    @Input() prefilledPromptsObs: Observable<AchExtraPrompts | null>;
    @Input() isFromEditSubmittedStandingAuth: boolean;
    @Output() extraPromptsComplete = new EventEmitter<boolean>();
    @Output() extraPromptsUpdatedInEdit = new EventEmitter();
  
    showTargetOfFunds: boolean;
    instructions$: Observable<StandingInstruction[]>;
    directionSelection$: Observable<'incoming' | 'outgoing' | ''>;
    isPtrs: boolean;
    isIip: boolean;
    subAccountId: string;
    isFromEdit: boolean = false;
    validInstructionsForIipAccount: boolean = true;
    standingAuthFormState: StandingAuthorizationForm;
    inquiryCallSuccessful: boolean = false;
    achInfo: AchInfo;
    private readonly controlNames = {
      standingAuthId: 'standingAuthId',
      transferDirection: 'transferDirection',
      frequency: 'frequency'
    };
    private _memoizedTransferDirection: 'incoming' | 'outgoing' | '';
    private destroy$ = new Subject<void>();
    formState$: Observable<ComponentState>;
    frequencySelectorModel: RadioControl = {
    title: 'Frequency',
    controlName: 'frequency',
    radios: [
      {
        value: 'onetime',
        label: ['One-time'],
      },
    ],
    validationMessage: 'Select a frequency'
  };
  
    constructor(private fb: UntypedFormBuilder, private store: Store) {}
  
    ngOnInit(): void {
      const achFormGroup = this.fb.group(
        {
          [this.controlNames.transferDirection]: [
            '',
            this.getControlValidators(this.controlNames.transferDirection),
          ],
        },
        {
          validators: validateDisabledFieldsInGroup(),
        }
      );
  
      this.parentForm.setControl('extraPrompts', achFormGroup);
  
      // We use take(2) to grab first 2 values from the values that the selector emits, one for true and one for false
      this.formState$ = this.store.select(selectStandingAuthForm).pipe(take(2));
  
      this.store
        .select(selectIsIip)
        .pipe(takeUntil(this.destroy$))
        .subscribe((isIip) => {
          this.isIip = isIip;
        });
  
      combineLatest([
        this.store.pipe(select(selectStandingAuthCallSucceededAch)),
        this.store.pipe(select(selectActiveAchIncomingInstructions)),
        this.transferDirectionControl.statusChanges,
      ])
        .pipe(takeUntil(this.destroy$), distinctUntilChanged())
        .subscribe(([callFinished, incomingInstructions, status]) => {
          const validControl = status === 'VALID' || status === 'DISABLED';
          if (validControl) {
            const controlName = this.controlNames.standingAuthId;
            if (!achFormGroup.contains(controlName)) {
              achFormGroup.setControl(
                controlName,
                new UntypedFormControl('', this.getControlValidators(controlName))
              );
            }
          }
  
          if (this.isIip && incomingInstructions.length === 0) {
            this.validInstructionsForIipAccount = false;
          }
  
          if (
            callFinished &&
            validControl &&
            this.validInstructionsForIipAccount
          ) {
            this.showTargetOfFunds = true;
          }
        });
  
      this.transferDirectionControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((direction) => {
          // reset source of funds component only when the value has actually changed (prefilled value
          // loading can overwrite the value auto-setting when only incoming/outgoing profiles are present)
          if (this._memoizedTransferDirection !== direction) {
            this._memoizedTransferDirection = direction;
            achFormGroup.get(this.controlNames.standingAuthId)?.reset('');
          }
  
          this.store.dispatch(StandingAuthFormActions.initializeServiceError());
        });
  
      // don't emit until the last prompt has been answered and the form status has changed
      this.extraPromptsGroup?.statusChanges
        .pipe(takeUntil(this.destroy$), distinctUntilChanged())
        .subscribe((val) => this.extraPromptsComplete.emit(val === 'VALID'));
  
      this.directionSelection$ = this.store.pipe(select(selectStandingAuthFormDirection));

      this.instructions$ = this.store.select(selectActiveOnRequestAndRecurringAchInstructions);

      this.store
        .select(selectIsPtrs)
        .pipe(takeUntil(this.destroy$))
        .subscribe((isPtrs) => {
          this.isPtrs = isPtrs;
        });
  
      this.store
        .select(selectSubAccountId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((subAccountId) => {
          this.subAccountId = subAccountId;
        });
  
      this.store
        .pipe(takeUntil(this.destroy$), select(selectIsFromEdit))
        .subscribe((isFromEdit) => (this.isFromEdit = isFromEdit));
  
      this.store
        .select(selectStandingAuthId)
        .pipe(
          takeUntil(this.destroy$),
          withLatestFrom(
            this.store.select(selectStandingAuthTableStartTransactionClickedACH)
          ),
          filter(([id, _startTransactionClicked]) => id !== '')
        )
        .subscribe(([startTransactionClicked]) => {
          if (!this.isFromEdit && !startTransactionClicked) {
            if (this.isPtrs) {
              this.store.dispatch(
                PtrsActions.callPtrs({ accountId: this.subAccountId })
              );
            }
          }
        });
  
      combineLatest([
        this.prefilledPromptsObs,
        this.store.select(selectStandingAuthTableEditTransactionClickedACH),
      ])
        .pipe(
          takeUntil(this.destroy$),          
          distinctUntilChanged()
        )
        .subscribe(([extraPrompts, editTransactionClicked]) => {          
          if (!!extraPrompts && editTransactionClicked) {
            buildFromPrefilledForm(extraPrompts, this.extraPromptsGroup);
          } else if (!extraPrompts?.standingAuthId && !editTransactionClicked) {
            buildFromPrefilledForm(
              this.prefilledPrompts,
              this.extraPromptsGroup,
              this.getControlValidators,
              this.extraPromptsUpdatedInEdit
            );
          }
        });
      
      this.store
        .select(selectAchInquiryCallFinished)
        .pipe(takeUntil(this.destroy$))
        .subscribe((inquiryCallSuccessful) => {
          this.inquiryCallSuccessful = inquiryCallSuccessful;
          if(inquiryCallSuccessful)
          {
            this.store.dispatch(
              StandingAuthorizationsTableActions.editTransactionFlowComplete()
            );
          }
        });

      this.extraPromptsGroup.disable();

      this.setFrequencyOptions();
    }
  
    ngOnDestroy(): void {
      this.destroy$?.next();
      this.destroy$?.unsubscribe();
    }
  
    get extraPromptsGroup(): UntypedFormGroup {
      return this.parentForm?.get('extraPrompts') as UntypedFormGroup;
    }
  
    get standingAuthIdControl(): UntypedFormControl {
      return this.extraPromptsGroup?.get(
        this.controlNames.standingAuthId
      ) as UntypedFormControl;
    }
  
    get transferDirectionControl(): UntypedFormControl {
      return this.extraPromptsGroup?.get(
        this.controlNames.transferDirection
      ) as UntypedFormControl;
    }

    get frequencyControl(): UntypedFormControl {
      return this.extraPromptsGroup?.get(
        this.controlNames.frequency
      ) as UntypedFormControl;
    }
  
    getControlValidators(controlName: string): ValidatorFn[] {
      switch (controlName) {
        case 'transferDirection':
          return [Validators.required, Validators.minLength(1)];
        case 'standingAuthId':
          return [Validators.required];
        case 'frequency':
          return [Validators.required];
      }
      return [];
    }

    private setFrequencyOptions(): void {
      if (!this.isPtrs) {
        this.frequencySelectorModel.radios.splice(1, 0, { value: 'recurring', label: ['Recurring'] });
      }
    }
  }
  
  function validateDisabledFieldsInGroup(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const fg = group as UntypedFormGroup;
      const errorMap: ValidationErrors = {};
  
      const controlNames = Object.keys(fg.controls);
      controlNames.forEach((name) => {
        const control = fg.controls[name];
        if (control?.disabled && typeof control?.validator === 'function') {
          if (Object.keys(control.validator(control) ?? []).length > 0) {
            errorMap.name = true;
          }
        }
      });
  
      return Object.keys(errorMap).length > 0 ? errorMap : null;
    };
  }